import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { authGuard, authMatchGuard } from './guards/auth.guard';
import { environment } from 'src/environments/environment';
import { permissionGuard, permissionMatchGuard } from './guards/permission.guard';
import { RESERVED_PERMISSIONS } from './constants/permission-catalog';

const routes: Routes = [
  {
    path: '',
    redirectTo: environment.CUSTOMIZATION.DEFAULT_ROUTE,
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginPageModule),
  },
  {
    path: 'login-alternative',
    loadChildren: () => import('./pages/login-alternative/login-alternative.module').then((m) => m.LoginAlternativePageModule),
  },
  {
    path: 'login-alternative-v',
    loadChildren: () => import('./pages/login-alternative-v/login-alternative-v.module').then((m) => m.LoginAlternativeVPageModule),
  },
  {
    path: 'lmap-rtls',
    loadChildren: () => import('./pages/lmap-rtls/lmap-rtls.module').then((m) => m.LmapRtlsPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view', },
  },
  {
    path: 'intrusion-detection',
    loadChildren: () => import('./pages/intrusion-detection/intrusion-detection.module').then( m => m.IntrusionDetectionPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'intrusion-detection.view' }
  },
  {
    path: 'devices',
    loadChildren: () => import('./pages/devices/devices.module').then((m) => m.DevicesPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'device-detail/:id',
    loadChildren: () => import('./pages/device-detail/device-detail.module').then((m) => m.DeviceDetailPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-worker',
    loadChildren: () => import('./pages/detail-worker/detail-worker.module').then((m) => m.DetailWorkerPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-vehicle',
    loadChildren: () => import('./pages/detail-vehicle/detail-vehicle.module').then((m) => m.DetailVehiclePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-stationary',
    loadChildren: () => import('./pages/detail-station/detail-station.module').then((m) => m.DetailStationPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-beaconControl',
    loadChildren: () => import('./pages/devices/devices.module').then((m) => m.DevicesPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-beacon',
    loadChildren: () => import('./pages/devices/devices.module').then((m) => m.DevicesPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-camera',
    loadChildren: () => import('./pages/devices/devices.module').then((m) => m.DevicesPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'detail-button',
    loadChildren: () => import('./pages/devices/devices.module').then((m) => m.DevicesPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'device-edition/:id',
    loadChildren: () => import('./pages/device-edition/device-edition.module').then((m) => m.DeviceEditionPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.edit' },
  },
  {
    path: 'device-historic',
    loadChildren: () => import('./pages/device-historic/device-historic.module').then((m) => m.DeviceHistoricPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view' },
  },
  {
    path: 'device-timeline/:id',
    loadChildren: () => import('./pages/device-timeline/device-timeline.module').then((m) => m.DeviceTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view.timeline' },
  },
  {
    path: 'rtls-status',
    loadChildren: () => import('./pages/rtls-status/rtls-status.module').then((m) => m.RtlsStatusPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view', },
  },
  {
    path: 'charts-sensors',
    loadChildren: () => import('./pages/charts-sensors/charts-sensors.module').then((m) => m.ChartsSensorsPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'environmental.view', },
  },
  {
    path: 'rtls',
    loadChildren: () => import('./pages/rtls/rtls.module').then((m) => m.RtlsPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view', },
  },
  {
    path: 'rtls-zone',
    loadChildren: () => import('./pages/rtls-zone/rtls-zone.module').then((m) => m.RtlsZonePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view', },
  },
  {
    path: 'tracking-count',
    loadChildren: () => import('./pages/tracking-count/tracking-count.module').then((m) => m.TrackingCountPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view', },
  },
  {
    path: 'blueprint-rtls',
    loadChildren: () => import('./pages/blueprint-rtls/blueprint-rtls.module').then((m) => m.BlueprintRtlsPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: ['tracking.view', 'environmental.view', 'ventilation.view'], },
  },
  {
    path: 'scada',
    loadChildren: () => import('./pages/scada-page/scada-page.module').then((m) => m.ScadaPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: ['emergency.view', 'network-monitoring.view', 'environmental.view', 'ventilation.view'], },
  },
  {
    path: 'main-menu-page',
    loadChildren: () => import('./pages/main-menu-page/main-menu-page.module').then((m) => m.MainMenuPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA main-menu
    // data: { permissions: ['tracking.view', 'environmental.view', 'ventilation.view'], },
  },
  {
    path: 'rtls-control',
    loadChildren: () => import('./pages/rtls-control/rtls-control.module').then((m) => m.RtlsControlPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.edit', },
  },
  {
    path: 'schedule-validation',
    loadChildren: () => import('./pages/schedule-validation/schedule-validation.module').then((m) => m.ScheduleValidationPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'historic',
    loadChildren: () => import('./pages/historic/historic.module').then((m) => m.HistoricPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view', },
  },
  {
    path: 'form-personnel-page',
    loadChildren: () => import('./pages/form-personnel-page/form-personnel-page.module').then((m) => m.FormPersonnelPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'form-machinery-page',
    loadChildren: () => import('./pages/form-machinery-page/form-machinery-page.module').then((m) => m.FormMachineryPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'workforce-form-page',
    loadChildren: () => import('./pages/workforce-form-page/workforce-form-page.module').then((m) => m.WorkforceFormPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'form-tasks-page',
    loadChildren: () => import('./pages/form-tasks-page/form-tasks-page.module').then((m) => m.FormTasksPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'form-summary-page',
    loadChildren: () => import('./pages/form-summary-page/form-summary-page.module').then((m) => m.FormSummaryPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'workforce-list-page',
    loadChildren: () => import('./pages/workforce-list-page/workforce-list-page.module').then((m) => m.WorkforceListPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'schedule-summary-page',
    loadChildren: () => import('./pages/schedule-summary-page/schedule-summary-page.module').then((m) => m.ScheduleSummaryPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'management-page',
    loadChildren: () => import('./pages/management-page/management-page.module').then((m) => m.ManagementPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'schedule-historic-page',
    loadChildren: () => import('./pages/schedule-historic-page/schedule-historic-page.module').then((m) => m.ScheduleHistoricPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'shift-elaboration-page',
    loadChildren: () => import('./pages/shift-elaboration-page/shift-elaboration-page.module').then((m) => m.ShiftElaborationPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'report-summary-page',
    loadChildren: () => import('./pages/report-summary-page/report-summary-page.module').then((m) => m.ReportSummaryPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for OSSA
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'modal-image',
    loadChildren: () => import('./pages/modal-image/modal-image.module').then((m) => m.ModalImagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define permissions for this page
    // data: { permission: 'tracking.edit', },
  },
  {
    path: 'privacy-policy',
    loadChildren: () => import('./pages/privacy-policy/privacy-policy.module').then((m) => m.PrivacyPolicyPageModule),
  },
  {
    path: 'device-upload',
    loadChildren: () => import('./pages/device-upload/device-upload.module').then((m) => m.DeviceUploadPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device.view.management', },
  },
  {
    path: 'device-location',
    loadChildren: () => import('./pages/device-location/device-location.module').then((m) => m.DeviceLocationPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.edit.location', },
  },
  {
    path: 'device-timeline',
    loadChildren: () => import('./pages/device-timeline/device-timeline.module').then((m) => m.DeviceTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view.timeline' },
  },
  {
    path: 'rtls-entrances',
    loadChildren: () => import('./pages/rtls-entrances/rtls-entrances.module').then((m) => m.RtlsEntrancesPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view' },
  },
  {
    path: 'historic-lmap-rtls',
    loadChildren: () => import('./pages/historic-list-page/historic-list-page.module').then((m) => m.HistoricListPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view' },
  },
  {
    path: 'menu-entrances-page',
    loadChildren: () => import('./pages/menu-entrances-page/menu-entrances-page.module').then((m) => m.MenuEntrancesPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view' },
  },
  {
    path: 'user-profile-page',
    loadChildren: () => import('./pages/user-profile-page/user-profile-page.module').then((m) => m.UserProfilePagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'user.view.profile' },
  },
  {
    path: 'admin',
    loadChildren: () => import('./pages/guide-admin-page/guide-admin-page.module').then((m) => m.GuideAdminPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'guide.view' },
  },
  {
    path: 'alerts-page/:flag',
    loadChildren: () => import('./pages/alerts-page/alerts-page.module').then((m) => m.AlertsPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'alarm.view' },
  },
  {
    path: 'dashboard-page',
    loadChildren: () => import('./pages/dashboard-page/dashboard-page.module').then((m) => m.DashboardPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: ['tracking.view', 'alarm.view'] },
  },
  {
    path: 'contact-page',
    loadChildren: () => import('./pages/contact-page/contact-page.module').then((m) => m.ContactPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // TODO: define if permissions are necessary
    // data: { permission: '' },
  },
  {
    path: 'mobile-coverage',
    loadChildren: () => import('./pages/mobile-coverage/mobile-coverage.module').then((m) => m.MobileCoveragePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: RESERVED_PERMISSIONS.mobileCoverageView },
  },
  {
    path: 'map',
    loadChildren: () => import('./pages/mobile-coverage-map/mobile-coverage-map.module').then((m) => m.MobileCoverageMapPageModule),
  },
  {
    path: 'main-map',
    loadChildren: () => import('./pages/map/map.module').then((m) => m.MapPageModule),
  },
  {
    path: 'home-map',
    loadChildren: () => import('./pages/map/map.module').then((m) => m.MapPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: RESERVED_PERMISSIONS.mapView },
  },
  {
    path: 'emergency-page',
    loadChildren: () => import('./pages/emergency-page/emergency-page.module').then((m) => m.EmergencyPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'emergency.view' },
  },
  {
    path: 'network-monitoring-page',
    loadChildren: () => import('./pages/network-monitoring-page/network-monitoring-page.module').then((m) => m.NetworkMonitoringPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'network-monitoring.view' },
  },
  {
    path: 'network-monitoring-timeline-page',
    loadChildren: () => import('./pages/network-monitoring-timeline-page/network-monitoring-timeline-page.module').then((m) => m.NetworkMonitoringTimelinePagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'network-monitoring.view']] },
  },
  {
    path: 'traffic-management',
    loadChildren: () => import('./pages/traffic-management/traffic-management.module').then((m) => m.TrafficManagementPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'traffic-management.view' },
  },
  {
    path: 'reset-password',
    loadChildren: () => import('./pages/reset-password/reset-password.module').then((m) => m.ResetPasswordPageModule),
  },
  {
    path: 'home-v',
    loadChildren: () => import('./pages/home-v/home-v.module').then((m) => m.HomeVPageModule),
  },
  {
    path: 'guide-menu',
    loadChildren: () => import('./pages/guide-menu/guide-menu.module').then((m) => m.GuideMenuPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'guide.view' },
  },
  {
    path: 'guide-page',
    loadChildren: () => import('./pages/guide-page/guide-page.module').then((m) => m.GuidePagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'guide.view' },
  },
  {
    path: 'diagnostic-page',
    loadChildren: () => import('./pages/diagnostic-page/diagnostic-page.module').then((m) => m.DiagnosticPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'guide.view' },
  },
  {
    path: 'info-guide-page',
    loadChildren: () => import('./pages/info-guide-page/info-guide-page.module').then((m) => m.InfoGuidePagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'guide.view' },
  },
  {
    path: 'guide-digimine-page',
    loadChildren: () => import('./pages/guide-digimine-page/guide-digimine-page.module').then((m) => m.GuideDigiminePagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'guide.view' },
  },
  {
    path: 'historic-alerts-page',
    loadChildren: () => import('./pages/historic-alerts-page/historic-alerts-page.module').then((m) => m.HistoricAlertsPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'alarm.view' },
  },
  {
    path: 'modal-gps-tracking',
    loadChildren: () => import('./pages/modal-gps-tracking/modal-gps-tracking.module').then((m) => m.ModalGpsTrackingPageModule),
  },
  {
    path: 'scada-ventilation-page',
    loadChildren: () => import('./pages/scada-ventilation-page/scada-ventilation-page.module').then((m) => m.ScadaVentilationPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'scada-ventilation-rtls-page',
    loadChildren: () => import('./pages/scada-ventilation-rtls-page/scada-ventilation-rtls-page.module').then((m) => m.ScadaVentilationRtlsPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'modal-traffic-light',
    loadChildren: () => import('./pages/modal-traffic-light/modal-traffic-light.module').then((m) => m.ModalTrafficLightPageModule),
  },
  {
    path: '3d-visor',
    loadChildren: () => import('./pages/three-visor/three-visor.module').then((m) => m.ThreeVisorPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: RESERVED_PERMISSIONS.threeDView },
  },
  {
    path: 'scada-ventilation-primary-page',
    loadChildren: () => import('./pages/scada-ventilation-primary-page/scada-ventilation-primary-page.module').then((m) => m.ScadaVentilationPrimaryPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'scada-ventilation-secondary-page',
    loadChildren: () => import('./pages/scada-ventilation-secondary-page/scada-ventilation-secondary-page.module').then((m) => m.ScadaVentilationSecondaryPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'scada-ventilation-damper-page',
    loadChildren: () => import('./pages/scada-ventilation-damper-page/scada-ventilation-damper-page.module').then((m) => m.ScadaVentilationDamperPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'modal-scada-ventilation-rtls',
    loadChildren: () => import('./pages/modal-scada-ventilation-rtls/modal-scada-ventilation-rtls.module').then((m) => m.ModalScadaVentilationRtlsPageModule),
  },
  {
    path: 'scada-ventilation-monitoring-page',
    loadChildren: () => import('./pages/scada-ventilation-monitoring-page/scada-ventilation-monitoring-page.module').then((m) => m.ScadaVentilationMonitoringPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'comms-failure',
    loadChildren: () => import('./pages/comms-failure/comms-failure.module').then((m) => m.CommsFailurePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'network-monitoring.view.failure-statuses' },
  },
  {
    path: 'chart-report/:type',
    loadChildren: () => import('./pages/chart-report/chart-report.module').then((m) => m.ChartReportPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: ['environmental.view', 'ventilation.view'] },
  },
  {
    path: 'modal-scada-ventilation-vm-detail',
    loadChildren: () => import('./pages/modal-scada-ventilation-vm-detail/modal-scada-ventilation-vm-detail.module').then((m) => m.ModalScadaVentilationVmDetailPageModule),
  },
  {
    path: 'modal-scada-ventilation-svg-device-location',
    loadChildren: () => import('./pages/modal-scada-ventilation-svg-device-location/modal-scada-ventilation-svg-device-location.module').then((m) => m.ModalScadaVentilationSvgDeviceLocationPageModule),
  },
  {
    path: 'scada-ventilation-reporting-page',
    loadChildren: () => import('./pages/scada-ventilation-reporting-page/scada-ventilation-reporting-page.module').then((m) => m.ScadaVentilationReportingPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'scada-ventilation-events-page',
    loadChildren: () => import('./pages/scada-ventilation-events-page/scada-ventilation-events-page.module').then((m) => m.ScadaVentilationEventsPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' },
  },
  {
    path: 'project-selector',
    loadChildren: () => import('./pages/project-selector/project-selector.module').then((m) => m.ProjectSelectorPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
  },
  {
    path: 'modal-worker-presence',
    loadChildren: () => import('./pages/modal-worker-presence/modal-worker-presence.module').then((m) => m.ModalWorkerPresencePageModule),
  },
  {
    path: 'modal-device-recovery',
    loadChildren: () => import('./pages/modal-device-recovery/modal-device-recovery.module').then((m) => m.ModalDeviceRecoveryPageModule),
  },
  {
    path: 'coming-soon',
    loadChildren: () => import('./pages/coming-soon/coming-soon.module').then((m) => m.ComingSoonPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
  },
  {
    path: 'operations-management-page',
    loadChildren: () => import('./pages/operations-management-page/operations-management-page.module').then((m) => m.OperationsManagementPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // 'role.view' would fit here, but for the moment it is restricted to superadmins
    data: { permissions: ['user.view', 'company.view', 'device.view.management', 'tracking.edit.location'] }
  },
  {
    path: 'fire-detection-page',
    loadChildren: () => import('./pages/fire-detection-page/fire-detection-page.module').then((m) => m.FireDetectionPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'fire-detection.view' }
  },
  {
    path: 'sensor-detection',
    loadChildren: () => import('./pages/sensor-detection-page/sensor-detection-page.module').then((m) => m.SensorDetectionPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'sensor-pressure.view' }
  },
  {
    path: 'sensor-detection-timeline',
    loadChildren: () => import('./pages/sensor-detection-timeline/sensor-detection-timeline.module').then((m) => m.SensorDetectionTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'sensor-pressure.view']] },
  },
  {
    path: 'access-control-page',
    loadChildren: () => import('./pages/access-control-page/access-control-page.module').then((m) => m.AccessControlPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'access-control.view' }
  },
  {
    path: 'e-vehicle-battery-management',
    loadChildren: () => import('./pages/e-vehicle-battery-management/e-vehicle-battery-management.module').then((m) => m.EVehicleBatteryManagementPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'e-vehicle.view' }
  },
  {
    path: 'device-capacity-page',
    loadChildren: () => import('./pages/device-capacity-page/device-capacity-page.module').then((m) => m.DeviceCapacityPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'device-capacity.view' }
  },
  {
    path: 'device-capacity-timeline',
    loadChildren: () => import('./pages/device-capacity-timeline/device-capacity-timeline.module').then((m) => m.DeviceCapacityTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'device-capacity.view']] },
  },
  {
    path: 'traffic-location-page',
    loadChildren: () => import('./pages/traffic-location-page/traffic-location-page.module').then((m) => m.TrafficlocationPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'traffic-management.view' }
  },
  {
    path: 'precision-test-page',
    loadChildren: () => import('./pages/precision-test-page/precision-test-page.module').then((m) => m.PrecisionTestPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'tracking.view' }
  },
  {
    path: 'energy-sensor-page',
    loadChildren: () => import('./pages/energy-sensor-page/energy-sensor-page.module').then((m) => m.EnergySensorPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'energy-sensor.view' }
  },
  {
    path: 'energy-sensor-timeline',
    loadChildren: () => import('./pages/energy-sensor-timeline/energy-sensor-timeline.module').then((m) => m.EnergySensorTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'energy-sensor.view']] },
  },
  {
    path: 'environmental-page',
    loadChildren: () => import('./pages/environmental-page/environmental-page.module').then((m) => m.EnvironmentalPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'environmental.view' }
  },
  {
    path: 'light-controller-page',
    loadChildren: () => import('./pages/light-controller-page/light-controller-page.module').then((m) => m.LightControllerPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'light-controller.view' }
  },
  {
    path: 'traffic-light-controller-page',
    loadChildren: () => import('./pages/traffic-light-controller-page/traffic-light-controller-page.module').then((m) => m.TrafficLightControllerPagePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'traffic-light.view' }
  },
  {
    path: 'evacuation-report',
    loadChildren: () => import('./pages/evacuation-report/evacuation-report.module').then((m) => m.EvacuationReportPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'evacuation-report.view' }
  },
  {
    path: 'evacuation-report-detail/:site/:id',
    loadChildren: () => import('./pages/evacuation-report-detail/evacuation-report-detail.module').then((m) => m.EvacuationReportDetailPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'evacuation-report.view' }
  },
  {
    path: 'ventilation-control',
    loadChildren: () => import('./pages/ventilation-control/ventilation-control.module').then(m => m.VentilationControlPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'ventilation.view' }
  },
  {
    path: 'water-treatment-plant',
    loadChildren: () => import('./pages/water-treatment-plant/water-treatment-plant.module').then(m => m.WaterTreatmentPlantPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'water-treatment-plant.view' }
  },
  {
    path: 'fire-detection-timeline',
    loadChildren: () => import('./pages/fire-detection-timeline/fire-detection-timeline.module').then((m) => m.FireDetectionTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'fire-detection.view']] },
  },
  {
    path: 'emergency-timeline',
    loadChildren: () => import('./pages/emergency-timeline/emergency-timeline.module').then((m) => m.EmergencyTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'emergency.view']] },
  },
  {
    path: 'environmental-timeline',
    loadChildren: () => import('./pages/environmental-timeline/environmental-timeline.module').then((m) => m.EnvironmentalTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'environmental.view']] },
  },
  {
    path: 'e-vehicle-battery-management-timeline',
    loadChildren: () => import('./pages/e-vehicle-battery-management-timeline/e-vehicle-battery-management-timeline.module').then((m) => m.EVehicleBatteryManagementTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'e-vehicle.view']] },
  },
  {
    path: 'lmap-rtls-timeline',
    loadChildren: () => import('./pages/lmap-rtls-timeline/lmap-rtls-timeline.module').then((m) => m.LmapRtlsTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'tracking.view']] },
  },
  {
    path: 'scada-ventilation-timeline',
    loadChildren: () => import('./pages/scada-ventilation-timeline/scada-ventilation-timeline.module').then((m) => m.ScadaVentilationTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'ventilation.view']] },
  },
  {
    path: 'ventilation-control-timeline',
    loadChildren: () => import('./pages/ventilation-control-timeline/ventilation-control-timeline.module').then((m) => m.VentilationControlTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'ventilation.view']] },
  },
  {
    path: 'bicomponent-status',
    loadChildren: () => import('./pages/bicomponent-status/bicomponent-status.module').then(m => m.BicomponentStatusPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'bicomponent.view' }
  },
  {
    path: 'bicomponent-status-timeline',
    loadChildren: () => import('./pages/bicomponent-status-timeline/bicomponent-status-timeline.module').then((m) => m.BicomponentStatusTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'bicomponent.view']] },
  },
  {
    path: 'water-treatment-plant-timeline',
    loadChildren: () => import('./pages/water-treatment-plant-timeline/water-treatment-plant-timeline.module').then((m) => m.WaterTreatmentPlantTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'water-treatment-plant.view']] },
  },
  {
    path: 'access-control-timeline',
    loadChildren: () => import('./pages/access-control-timeline/access-control-timeline.module').then((m) => m.AccessControlTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'access-control.view']] },
  },
  {
    path: 'smoke-control',
    loadChildren: () => import('./pages/smoke-control/smoke-control.module').then(m => m.SmokeControlPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'smoke-control.view' }
  },
  {
    path: 'smoke-control-timeline',
    loadChildren: () => import('./pages/smoke-control-timeline/smoke-control-timeline.module').then((m) => m.SmokeControlTimelinePageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permissions: [['timeline.view', 'smoke-control.view']] },
  },
  {
    path: 'camera-viewer-page',
    loadChildren: () => import('./pages/camera-viewer/camera-viewer-page.module').then(m => m.CameraViewerPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'camera-viewer.view' }
  },
  {
    path: 'geo-dashboard',
    loadChildren: () => import('./pages/geo-dashboard/geo-dashboard.module').then( m => m.GeoDashboardPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // Sole landing of the project-based deployments (their SIDEMENU is empty), so every role on
    // them is expected to hold this key: without it the project selector has nowhere to forward to.
    data: { permission: 'geotechnical.view' },
  },
  {
    path: 'project-management',
    loadChildren: () => import('./pages/project-management-page/project-management-page.module').then((m) => m.ProjectManagementPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // One pair per tab of the page (projects, zones, units, devices): seeing the entity and
    // being able to write it, since every action here is a write. Holding a whole pair opens
    // the page, on the tab that pair unlocks; the tab bar applies the same rule.
    data: {
      permissions: [
        ['project.view', 'project.create'],
        ['project.view', 'project.edit'],
        ['zone.view', 'zone.create'],
        ['zone.view', 'zone.edit'],
        ['section.view', 'section.create'],
        ['section.view', 'section.edit'],
        ['section.view.devices', 'device.create'],
        ['section.view.devices', 'device.edit'],
      ],
    },
  },
  {
    path: 'geo-device/:id',
    loadChildren: () => import('./pages/geo-device-detail/geo-device-detail.module').then( m => m.GeoDeviceDetailPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    // Either half of the page justifies opening it: the reading list or the charts.
    // Which of the two a role actually sees is gated inside the page.
    data: { permissions: ['geotechnical.view.readings', 'geotechnical.view.charts'] },
  },
  {
    path: 'training-management',
    loadChildren: () => import('./pages/training-management/training-management.module').then((m) => m.TrainingManagementPageModule),
    canActivate: [authGuard, permissionGuard],
    canMatch: [authMatchGuard, permissionMatchGuard],
    data: { permission: 'training.view' }
  },
  {
    path: '**',
    redirectTo: environment.CUSTOMIZATION.DEFAULT_ROUTE,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
